import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { AuthUser } from '../api/auth';
import * as authApi from '../api/auth';
import { AUTH_TOKEN_STORAGE_KEY } from '../api/client';

type AuthState = {
  token: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  login: (params: { email: string; password: string }) => Promise<void>;
  signup: (params: { first_name: string; last_name: string; email: string; password: string; password_confirmation?: string; accepted_terms?: boolean }) => Promise<{ message: string; email: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(AUTH_TOKEN_STORAGE_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(!!token);

  const loadMe = useCallback(async (tokenOverride?: string) => {
    const effectiveToken = tokenOverride ?? token;
    if (!effectiveToken) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const me = await authApi.me(effectiveToken);
      setUser(me);
    } catch (err) {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Only run on mount to load initial user if token exists in localStorage
  useEffect(() => {
    const initialToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (initialToken) {
      void loadMe(initialToken);
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (params: { email: string; password: string }) => {
    const { token } = await authApi.login(params);
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    setToken(token);
    await loadMe(token);
  }, [loadMe]);

  const signup = useCallback(async (params: { first_name: string; last_name: string; email: string; password: string; password_confirmation?: string }) => {
    const result = await authApi.signup(params);
    // Don't automatically log in - user needs to confirm email first
    return result;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      setToken(null);
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await loadMe();
  }, [loadMe]);

  const value = useMemo<AuthState>(() => ({
    token,
    user,
    isLoading,
    login,
    signup,
    logout,
    refreshUser,
  }), [token, user, isLoading, login, signup, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
